import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { StaffService } from '../../../../services/staff.service';
import { FNFResponse } from '../../../../models/response';

@Component({
  selector: 'app-staff-pin-form',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIcon],
  templateUrl: './staff-pin-form.component.html',
  styleUrl: './staff-pin-form.component.css',
})
export class StaffPinFormComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  private staffService = inject(StaffService);

  staff: any = null;
  loading = true;

  readonly pinIndices = [0, 1, 2, 3, 4, 5];
  newPinDigits: string[] = ['', '', '', '', '', ''];
  confirmPinDigits: string[] = ['', '', '', '', '', ''];
  showNewPin = false;
  showConfirmPin = false;
  confirmPinError = false;

  ngOnInit() {
    this.route.paramMap.subscribe((params) => this.load(params.get('id')));
  }

  private load(userId: string | null) {
    this.loading = true;
    this.staffService.getAllStaffs().subscribe({
      next: (response: FNFResponse<any>) => {
        this.staff =
          (response?.data || []).find((s: any) => String(s.user_id) === String(userId)) || null;
        this.loading = false;
        if (!this.staff) {
          this.toastr.error('That staff member could not be found.', 'Error');
          this.cancel();
        }
      },
      error: () => {
        this.toastr.error('Unable to load the staff member. Please try again later.', 'Error');
        this.loading = false;
        this.cancel();
      },
    });
  }

  get newPin(): string {
    return this.newPinDigits.join('');
  }

  get confirmPin(): string {
    return this.confirmPinDigits.join('');
  }

  get isFormValid(): boolean {
    return (
      /^\d{6}$/.test(this.newPin) &&
      /^\d{6}$/.test(this.confirmPin) &&
      this.newPin === this.confirmPin
    );
  }

  onPinDigitInput(field: 'new' | 'confirm', index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    const digits = field === 'new' ? this.newPinDigits : this.confirmPinDigits;
    digits[index] = value;
    if (field === 'new') {
      this.newPinDigits = [...digits];
    } else {
      this.confirmPinDigits = [...digits];
    }
    this.refreshMatchError();
    input.value = value;
    if (value && index < 5) {
      (input.nextElementSibling as HTMLInputElement)?.focus();
    }
  }

  onPinKeydown(field: 'new' | 'confirm', index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const digits = field === 'new' ? this.newPinDigits : this.confirmPinDigits;
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      (input.previousElementSibling as HTMLInputElement)?.focus();
    }
  }

  onPinPaste(field: 'new' | 'confirm', event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    const digits = field === 'new' ? this.newPinDigits : this.confirmPinDigits;
    for (let i = 0; i < 6; i++) {
      digits[i] = pasted[i] ?? '';
    }
    if (field === 'new') {
      this.newPinDigits = [...digits];
    } else {
      this.confirmPinDigits = [...digits];
    }
    this.refreshMatchError();

    const inputs = (event.target as HTMLInputElement).parentElement?.querySelectorAll('input');
    if (inputs?.length && pasted.length > 0) {
      (inputs[Math.min(pasted.length, 5)] as HTMLInputElement).focus();
    }
  }

  /** Only complain once both are fully entered; mid-typing is not an error. */
  private refreshMatchError() {
    this.confirmPinError =
      this.newPin.length === 6 && this.confirmPin.length === 6 && this.newPin !== this.confirmPin;
  }

  toggleShowNewPin(): void {
    this.showNewPin = !this.showNewPin;
  }

  toggleShowConfirmPin(): void {
    this.showConfirmPin = !this.showConfirmPin;
  }

  cancel(): void {
    this.router.navigate(['/main/staffs']);
  }

  save(): void {
    if (!this.isFormValid) {
      this.refreshMatchError();
      return;
    }

    this.spinner.show();
    this.staffService.setPinStaff(this.staff.user_id, { new_pin: this.newPin }).subscribe({
      next: (response: FNFResponse<any>) => {
        this.spinner.hide();
        if (!response.data) {
          const message = response.error?.error?.message || response.error?.message;
          this.toastr.error(message || response.message || 'No data found!', 'Error');
          return;
        }
        this.toastr.success(response.data.message || 'PIN updated successfully', 'Success');
        this.cancel();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
